// EntryGate — picks one of four pipelines (Manual / Wizard / Photo /
// Scan). Re-laid-out for the rebuild: hero illustration at top,
// big tappable cards below, no chip-row pattern.
//
// Each card carries the SVG that pipeline already owns:
//   - upc_scanner.svg for the scan pipeline
//   - shopping_cart.svg for receipts (photo path)
//   - The progression Step1 avatar for the manual / wizard cards
//
// Why illustrations instead of icons + emoji: the user explicitly
// asked for the AddItem flow to feel "magical" rather than dated.
// Custom SVGs are the brand voice; emoji is everyone else's.

import { font, color, radius } from "../../../lib/mcmTokens";
import { CHIP_TONES } from "../../../lib/tokens";

/**
 * Props:
 *   onPick — (mode: "manual" | "wizard" | "photo" | "scan") => void
 *   onClose — () => void.
 *   suggestion — optional copy line above the cards.
 *
 * Scan was reinstated 2026-05-02 as a first-class pipeline — see
 * additem/ScanPipeline.jsx. The earlier removal note (silent canonical
 * binds, OFF/USDA disagreements, modal sizing issues) is now addressed
 * by the new flow: explicit scan-review surface, AI-driven canonical
 * proposal popup with editable name, raw-data visibility panel.
 */
export default function EntryGate({ onPick, onClose, suggestion }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, flex: 1, minHeight: 0 }}>
      <Hero suggestion={suggestion} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <ChoiceCard
          iconUrl="/icons/upc_scanner.svg"
          tone={CHIP_TONES.canonical}
          kicker="Scan"
          title="Scan a barcode"
          subtitle="Fastest path. We pair it with what we know."
          onClick={() => onPick?.("scan")}
        />
        <ChoiceCard
          iconUrl="/icons/AddItemProgression/Step4.svg"
          tone={CHIP_TONES.cut}
          kicker="Browse"
          title="Pick from your kitchen"
          subtitle="Fridge, pantry, freezer — search or drill in."
          onClick={() => onPick?.("wizard")}
        />
        <ChoiceCard
          iconUrl="/icons/receipt_scanner.svg"
          tone={CHIP_TONES.state}
          kicker="Photo"
          title="Snap the package"
          subtitle="Show us the front of the package."
          onClick={() => onPick?.("photo")}
        />
      </div>
      {onClose && (
        <button
          onClick={(e) => { e.preventDefault(); onClose(); }}
          style={{
            alignSelf: "center",
            marginTop: 4,
            padding: "10px 18px",
            fontFamily: font.sans,
            fontSize: 12,
            color: color.inkMuted,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            letterSpacing: "0.06em",
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
}

function Hero({ suggestion }) {
  return (
    <div style={{
      flexShrink: 0,
      padding: "20px 18px 14px",
      borderRadius: radius.card || 18,
      background: `linear-gradient(180deg, ${color.glassFillHeavy} 0%, ${color.cream} 100%)`,
      border: `1px solid ${color.hairline}`,
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}>
      <div style={{
        fontFamily: font.mono,
        fontSize: 10,
        letterSpacing: "0.20em",
        textTransform: "uppercase",
        color: CHIP_TONES.canonical.fg,
      }}>
        Add to kitchen
      </div>
      <h2 style={{
        margin: 0,
        fontFamily: font.itemTitle,
        fontStyle: "italic",
        fontWeight: 500,
        fontSize: 30,
        lineHeight: 1.05,
        color: color.ink,
      }}>
        How are you adding it?
      </h2>
      {suggestion ? (
        <div style={{
          marginTop: 4,
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 14,
          color: color.inkMuted,
        }}>
          {suggestion}
        </div>
      ) : null}
    </div>
  );
}

function ChoiceCard({ iconUrl, tone, kicker, title, subtitle, onClick }) {
  return (
    <button
      onClick={(e) => { e.preventDefault(); onClick?.(); }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        width: "100%",
        minHeight: 88,
        padding: "14px 16px",
        textAlign: "left",
        background: color.cream,
        border: `1px solid ${color.hairline}`,
        borderRadius: radius.card || 16,
        cursor: "pointer",
        transition: "background 160ms ease, border-color 160ms ease",
      }}
    >
      <div style={{
        flexShrink: 0,
        width: 60,
        height: 60,
        borderRadius: radius.field || 14,
        background: `${tone.fg}1a`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}>
        <img
          src={iconUrl}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            padding: 6,
          }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{
          fontFamily: font.mono,
          fontSize: 9,
          letterSpacing: "0.20em",
          textTransform: "uppercase",
          color: tone.fg,
          fontWeight: 600,
        }}>
          {kicker}
        </div>
        <div style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 19,
          color: color.ink,
          lineHeight: 1.1,
        }}>
          {title}
        </div>
        <div style={{
          fontFamily: font.sans,
          fontSize: 12,
          color: color.inkMuted,
          lineHeight: 1.3,
        }}>
          {subtitle}
        </div>
      </div>
      <svg
        width="14" height="14" viewBox="0 0 14 14"
        style={{ flexShrink: 0, color: tone.fg, opacity: 0.5 }}
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <polyline points="5 2 10 7 5 12" />
      </svg>
    </button>
  );
}
